import { IDataModelItem, IDataModelSchema } from '../types/data-model';
import { requestData } from './api';
import { IDataModelSampleItem } from '../types';
import { QueryClient, useQuery } from 'react-query';
import { constants, EReactQueryKey } from '../utils/common';
import { getColumnDescription } from '../utils/pipeline';

/**
 * list all data models
 * @returns
 */
export async function requestModelList(): Promise<IDataModelItem[]> {
  const data = (await requestData({
    ...{
      method: 'get',
      url: '/v1.0/models',
      showErrorMessage: true,
      timeoutErrorMessage: 'request data-model list fail',
    },
  })) as {
    dataModelDTO: IDataModelItem;
    activePipelineCount: number;
  }[];
  return (
    (data ? data : [])
      .map((it) => {
        const { dataModelDTO, activePipelineCount } = it;
        return {
          ...dataModelDTO,
          activePipelineCount,
        };
      })
      // check dataModel validation
      .filter((it) => {
        return Boolean(it.uuid && Array.isArray(it.versions) && it.versions.length > 0);
      })
  );
}
export function useRequestModelList() {
  return useQuery(EReactQueryKey.DATA_MODEL_LIST, requestModelList);
}
export async function requestAndCacheModelList(queryClient: QueryClient): Promise<IDataModelItem[]> {
  let dataModelList: IDataModelItem[] | undefined = queryClient.getQueryData(EReactQueryKey.DATA_MODEL_LIST);
  if (dataModelList) {
    return dataModelList;
  }
  dataModelList = await requestModelList();
  if (dataModelList) {
    queryClient.setQueryData(EReactQueryKey.DATA_MODEL_LIST, dataModelList);
  }
  return dataModelList;
}

export function parseSchemaFromStr(schemaStr: string): IDataModelSchema[] {
  // name=dt, type=DATE, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null
  function parseItem(schemaItemStr: string): IDataModelSchema {
    const itemReg = /([^=,]+)=([^=,]+)(?:, *|$)/g;
    let parseItemRes: RegExpExecArray | null = null;
    let res: IDataModelSchema = {
      name: '',
      type: 'STRING',
      mode: 'NULLABLE',
      description: '',
    };
    while ((parseItemRes = itemReg.exec(schemaItemStr)) !== null) {
      // console.log(parseFieldRes[1]);
      // parseItem(parseItemRes[1]);
      const [, key, value] = parseItemRes;
      if (value === 'null') {
        // @ts-ignore
        res[key] = null;
      } else {
        // @ts-ignore
        res[key] = value;
      }
    }
    return res;
  }
  const findFieldsReg = /schema *{ *fields *= *\[(.*)\]}/i;
  const findFieldsRes = findFieldsReg.exec(schemaStr);
  if (!findFieldsRes) {
    throw new Error(`parse schemaStr fail`);
  }
  const filedsStr = findFieldsRes[1];
  // console.log(filedsStr);
  const parseFieldReg = /Field{(.*?)},?/g;
  let parseFieldRes: RegExpExecArray | null = null;

  const res: IDataModelSchema[] = [];
  while ((parseFieldRes = parseFieldReg.exec(filedsStr)) !== null) {
    res.push(parseItem(parseFieldRes[1]));
  }
  // console.log(res);
  return res;
}

/**
 * NOTICE: useRequestModelSchema is recommanded, as it is wrapped by react-query
 */
export async function requestModelSchema(config: {
  urlParams: {
    modelId: string;
    version: string;
  };
}): Promise<{
  modeUuid: string;
  list: IDataModelSchema[];
}> {
  const {
    urlParams: { modelId },
  } = config;
  /** fix for uuid with value null, '', undefined */
  if (!modelId) {
    return { modeUuid: modelId, list: [] };
  }
  const { modeUuid, schema } = (await requestData({
    ...{
      method: 'get',
      url: '/v1.0/models/:modelId/schema/:version',
      timeoutErrorMessage: 'request data-model schema fail',
      showErrorMessage: true,
    },
    ...config,
  })) as {
    modeUuid: string;
    /**
     * format of Schema, decomposed by frontend, may be it is better structured by backend
     * Schema{fields=[Field{name=viewerId, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=asset, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=device_os, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=country, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=state, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=city, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=asn, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=isp, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=start_time_unix_time, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=startup_time_ms, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=playing_time_ms, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=buffering_time_ms, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=interrupts, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=average_bitrate_kbps, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=startup_error, type=BOOLEAN, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=session_tags, type=RECORD, mode=REPEATED, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=ip_address, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=cdn, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=browser, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=conviva_session_id, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=stream_url, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=error_list, type=RECORD, mode=REPEATED, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=percentage_complete, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=connection_induced_rebuffering_time_ms, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=video_restart_time_ms, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=rejoined_count, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=VPF, type=BOOLEAN, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=VPF_error_list, type=RECORD, mode=REPEATED, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=content_length_ms, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=ended_status, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=end_time_unix_time, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=VSF_B, type=BOOLEAN, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=VSF_B_Error_List, type=RECORD, mode=REPEATED, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=VSF_T, type=BOOLEAN, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=VSF_T_Error_List, type=RECORD, mode=REPEATED, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=VPF_B, type=BOOLEAN, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=VPF_B_Error_List, type=RECORD, mode=REPEATED, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=VPF_T, type=BOOLEAN, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=VPF_T_Error_List, type=RECORD, mode=REPEATED, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=dt, type=TIMESTAMP, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=cust, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=Micro_Playing_Time_ms, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=Micro_Playing_Interruptions, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=Micro_Buffering_Time_ms, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=Micro_Buffering_Interruptions, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=Long_Rebuffering_Time_ms, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=Long_Rebuffering_Interruptions, type=INTEGER, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=last_cdn_edge_server, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}, Field{name=last_cdn_groupid, type=STRING, mode=NULLABLE, description=null, policyTags=null, maxLength=null, scale=null, precision=null}]}"
     */
    schema: string;
  };
  // console.log(schemaRes);
  return { modeUuid, list: parseSchemaFromStr(schema) };
}
export function useRequestModelSchema(modelId: string, version: string) {
  return useQuery([EReactQueryKey.DATA_MODEL_SCHEMA, modelId, version], async () => {
    const data = await requestModelSchema({
      urlParams: {
        modelId: modelId,
        version,
      },
    });
    data.list?.forEach((it) => {
      const description = getColumnDescription({ columnName: it.name });
      it.description = description.description;
    });
    return data.list;
  });
}
export async function requestAndCacheModelSchema(
  queryClient: QueryClient,
  modelId: string,
  version: string,
): Promise<IDataModelSchema[]> {
  let schema: IDataModelSchema[] | undefined = queryClient.getQueryData([
    EReactQueryKey.DATA_MODEL_SCHEMA,
    modelId,
    version,
  ]);
  if (schema) {
    return schema;
  }
  const data = await requestModelSchema({
    urlParams: {
      modelId: modelId,
      version,
    },
  });
  schema = data.list;
  if (schema) {
    queryClient.setQueryData([EReactQueryKey.DATA_MODEL_SCHEMA, modelId, version], schema);
  }
  return schema;
}

interface IRequestModelSampleParams {
  urlParams: {
    modelId: string;
    version: string;
  };
  params: {
    // default 1
    pageNum?: number;
    // default 10
    pageSize?: number;
    /** as limit in sql, default 100 */
    limitNum?: number;
    customerId?: string;
    customerName?: string;
  };
}

export async function requestModelSample(config: IRequestModelSampleParams): Promise<{
  pageNum: number;
  pageSize: number;
  pages: number;
  total: number;
  sampleDataList: IDataModelSampleItem[];
}> {
  const {
    urlParams: { modelId, version },
    params: { pageSize, customerId },
  } = config;
  const emptyData = {
    pageNum: 1,
    pageSize: pageSize ? pageSize : 10,
    pages: 1,
    total: 0,
    sampleDataList: [],
  };
  if (modelId === constants.EMPTY_MODEL_ID || version === constants.EMPTY_VERSIN) {
    return emptyData;
  }
  /** if customerId is not set, return empty data list */
  if (!customerId) {
    return emptyData;
  }
  const data = (await requestData({
    ...{
      method: 'get',
      url: '/v1.0/models/:modelId/sample/:version',
      showErrorMessage: true,
      timeoutErrorMessage: 'request data-model preview fail',
    },
    ...config,
  })) as {
    pageNum: number;
    pageSize: number;
    pages: number;
    total: number;
    entities: {
      sampleRowData: IDataModelSampleItem;
    }[];
  };
  return {
    ...data,
    sampleDataList: data.entities.map((it) => it.sampleRowData),
  };
}

export function useRequestModelSample(config: IRequestModelSampleParams) {
  const {
    urlParams: { modelId, version },
    params: { pageNum, pageSize, limitNum, customerId, customerName },
  } = config;
  return useQuery([modelId, version, pageNum, pageSize, limitNum, customerId, customerName], async () => {
    return await requestModelSample(config);
  });
}
